/**
 * Sign-in (FR-ACC-03): e-mail or username + password, then the authenticator code for
 * privileged roles. First sign-in: choose a password, then (where required) enrol an
 * authenticator; recovery codes are shown once. 5 failed attempts lock the account
 * for 15 minutes — the screen says so plainly and when to try again.
 */
import { useMutation } from '@tanstack/react-query';
import { Lock, LogIn } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Button, ErrorNote, Input, Note } from '../../components/ui';
import { BRAND } from '../../config/brand';
import { ApiError, api, deviceKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { EP } from '../../lib/endpoints';
import { time } from '../../lib/format';
import type { LoginResponse, SessionResponse } from '../../lib/types';
import { TagGlyph } from '../shell/Wordmark';

interface Setup {
  secret: string;
  otpauthUri: string;
  qrDataUrl: string;
}

/** Sign-in and account screens: enterprise split layout (boss, 11 Oct). Left: vault photo (Kling AI) with
 *  the product promise; right: the hang-tag card. Below 1024 px the photo becomes a short banner. The
 *  landing page says "Pawnshop Management System", never the internal EROS name (boss, 11 Oct). */
const PRODUCT_NAME = 'Pawnshop Management System';
const POINTS: [string, string][] = [
  ['RFID audits in minutes', 'Sweep a whole branch with a handheld; blind counts, recounts and approvals.'],
  ['Pawn, sales and stock on one ledger', 'Tickets, interest, renewals, forfeitures, transfers and cash-up, every peso traced.'],
  ['Tamper-proof by design', 'Approvals by a second person, a hash-chained activity log and encrypted records.'],
];

export function Frame({ title, sub, children }: { title: string; sub?: ReactNode; children: ReactNode }) {
  return (
    <div className="grid min-h-dvh bg-paper lg:grid-cols-[minmax(0,1.1fr)_minmax(420px,1fr)]">
      {/* Brand panel */}
      <aside className="relative isolate overflow-hidden bg-[rgb(10_32_26)] text-white">
        <img src="/brand/login-hero.webp" alt="" aria-hidden className="absolute inset-0 -z-10 h-full w-full object-cover" />
        <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-t from-[rgb(6_22_18/0.96)] via-[rgb(6_22_18/0.72)] to-[rgb(6_22_18/0.35)]" />
        <div className="flex h-full flex-col justify-between gap-8 px-6 py-6 sm:px-10 sm:py-8 lg:px-14 lg:py-12">
          <div className="flex items-center gap-3">
            <TagGlyph className="h-8 w-8" />
            <div className="leading-tight">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[rgb(var(--brass))]">Enterprise</p>
              <p className="text-base font-semibold">{PRODUCT_NAME}</p>
            </div>
          </div>
          <div className="hidden max-w-2xl lg:block">
            <h2 className="display text-[2.1rem] leading-[1.15] xl:text-[2.6rem]"><span className="whitespace-nowrap">Every item. Every branch.</span><br />Accounted for.</h2>
            <p className="mt-4 text-lg text-white/80">One secure system for inventory, pawn loans, sales and audits across all your branches.</p>
            <ul className="mt-8 space-y-5">
              {POINTS.map(([t, d]) => (
                <li key={t} className="flex gap-4">
                  <span aria-hidden className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 bg-[rgb(var(--brass))]" />
                  <span><span className="block font-semibold">{t}</span><span className="text-white/75">{d}</span></span>
                </li>
              ))}
            </ul>
          </div>
          <p className="hidden text-sm text-white/60 lg:block">Encrypted · Two-step sign-in · Full audit trail · Operated by {BRAND.operator}</p>
        </div>
      </aside>

      {/* Sign-in panel */}
      <main className="relative grid place-items-center px-4 py-10 sm:px-8">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgb(var(--ink)) 0 1px, transparent 1px 32px)' }} />
        <div className="relative w-full max-w-[420px]">
          <svg aria-hidden viewBox="0 0 400 80" preserveAspectRatio="xMidYMax meet" className="relative z-20 mx-auto -mb-[54px] block h-[80px] w-full">
            <path d="M200 72 C 176 40, 130 14, 70 0" fill="none" stroke="rgb(var(--brass))" strokeWidth="2" strokeLinecap="round" />
            <path d="M200 72 C 226 42, 280 16, 340 0" fill="none" stroke="rgb(var(--brass))" strokeWidth="2" strokeLinecap="round" opacity=".55" />
          </svg>
          <div className="tag-outline relative">
            <span aria-hidden className="tag-eyelet absolute left-1/2 top-[17px] z-10 h-[18px] w-[18px] -translate-x-1/2 rounded-full" />
            <div className="tag-card bg-card px-6 pb-7 pt-14 shadow-lift sm:px-8">
              <p className="mb-5 text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted">{BRAND.clientName || PRODUCT_NAME}</p>
              <h1 className="text-center text-2xl font-semibold">{title}</h1>
              {sub && <div className="mt-1 text-center text-sm text-muted">{sub}</div>}
              <div className="mt-6">{children}</div>
            </div>
          </div>
          <p className="mt-6 text-center text-sm text-muted">Need access? Ask your administrator · <a className="underline" href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a></p>
        </div>
      </main>
    </div>
  );
}

const codeInput = (v: string) => v.replace(/[^\dA-Za-z-]/g, '').slice(0, 20);

/** Lockout (code LOCKED, HTTP 429/423) gets its own message with the unlock time. */
function LoginError({ error }: { error: unknown }) {
  if (error instanceof ApiError && (error.code === 'LOCKED' || error.code === 'ACCOUNT_LOCKED' || error.status === 423)) {
    const until = error.body.lockedUntil as string | undefined;
    return (
      <Note tone="danger" icon={<Lock aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />}>
        <p className="font-semibold">This account is locked for 15 minutes</p>
        <p className="mt-0.5">
          Too many wrong passwords. {until ? `Try again after ${time(until)}.` : 'Try again later.'} If it was not you, tell your administrator.
        </p>
      </Note>
    );
  }
  if (error instanceof ApiError && typeof error.body.attemptsLeft === 'number') {
    const n = error.body.attemptsLeft;
    return (
      <Note tone="danger">
        {error.message} {n > 0 && `${n} ${n === 1 ? 'try' : 'tries'} left before the account locks.`}
      </Note>
    );
  }
  return <ErrorNote error={error} />;
}

export function SignIn() {
  const { adopt, endedReason } = useAuth();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const signIn = useMutation({
    mutationFn: () => api<LoginResponse>(EP.auth.login, { method: 'POST', body: { login: login.trim(), password, deviceKey: deviceKey() }, anonymous: true }),
    onSuccess: (r) => (r.mfaRequired ? setMfaToken(r.mfaToken) : adopt(r)),
  });
  const verify = useMutation({
    mutationFn: () => api<SessionResponse>(EP.auth.mfa, { method: 'POST', body: { mfaToken, code: code.trim() }, anonymous: true }),
    onSuccess: adopt,
  });

  if (mfaToken) {
    return (
      <Frame title="Enter your authenticator code" sub="Open your authenticator app and type the 6-digit code. Lost your phone? Use one of your recovery codes.">
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); verify.mutate(); }} className="space-y-4">
          <Input label="Code" autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(codeInput(e.target.value))} placeholder="123456" className="[&_input]:code [&_input]:text-center [&_input]:text-xl [&_input]:tracking-[0.3em]" />
          <LoginError error={verify.error} />
          <Button type="submit" size="lg" block loading={verify.isPending} disabled={code.length < 6}>
            Verify
          </Button>
          <Button variant="quiet" block onClick={() => { setMfaToken(null); setCode(''); verify.reset(); }}>
            Use a different account
          </Button>
        </form>
      </Frame>
    );
  }

  return (
    <Frame title="Sign in">
      {endedReason === 'idle' && (
        <Note tone="info" className="mb-4">
          You were signed out after a period of no activity. Sign in again to continue.
        </Note>
      )}
      {endedReason === 'expired' && (
        <Note tone="info" className="mb-4">
          Your session ended. Sign in again to continue.
        </Note>
      )}
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); signIn.mutate(); }} className="space-y-4">
        <Input label="E-mail or username" autoComplete="username" autoCapitalize="none" autoFocus value={login} onChange={(e) => setLogin(e.target.value)} />
        <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <LoginError error={signIn.error} />
        <Button type="submit" size="lg" block loading={signIn.isPending} disabled={!login || !password} icon={<LogIn aria-hidden className="h-4 w-4" />}>
          Sign in
        </Button>
      </form>
      <p className="mt-5 text-center text-sm text-muted">Forgot your password? Ask your branch manager or administrator to reset it.</p>
    </Frame>
  );
}

export function PasswordForm({ onDone, submitLabel = 'Save password', temporary }: { onDone: () => void; submitLabel?: string; temporary?: boolean }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const change = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>(EP.auth.password, { method: 'POST', body: { currentPassword: current, newPassword: next } }),
    onSuccess: onDone,
  });
  const mismatch = again.length > 0 && next !== again;
  return (
    <form onSubmit={(e) => { e.preventDefault(); change.mutate(); }} className="space-y-4">
      <Input label={temporary ? 'Temporary password' : 'Current password'} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
      <Input label="New password" type="password" autoComplete="new-password" hint="At least 10 characters. A short sentence is easy to remember and hard to guess." value={next} onChange={(e) => setNext(e.target.value)} error={change.error instanceof ApiError ? change.error.fields.newPassword : undefined} />
      <Input label="New password again" type="password" autoComplete="new-password" value={again} error={mismatch ? 'The passwords do not match.' : undefined} onChange={(e) => setAgain(e.target.value)} />
      {!(change.error instanceof ApiError && change.error.fields.newPassword) && <ErrorNote error={change.error} />}
      <Button type="submit" size="lg" block loading={change.isPending} disabled={!current || next.length < 10 || next !== again}>
        {submitLabel}
      </Button>
    </form>
  );
}

/** Forced on first sign-in or after an admin reset. */
export function SetPassword() {
  const { user, signOut } = useAuth();
  return (
    <Frame title="Choose your password" sub={`${user?.name ?? ''}, replace the temporary password with your own. You will then sign in again.`}>
      <PasswordForm temporary onDone={() => void signOut()} />
      <Button variant="quiet" block className="mt-2" onClick={() => void signOut()}>
        Sign out
      </Button>
    </Frame>
  );
}

/** Authenticator enrolment, required for Owner/Admin, Controller, Manager and Auditor. */
export function MfaSetup() {
  const { adopt, signOut } = useAuth();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<({ recoveryCodes: string[] } & SessionResponse) | null>(null);
  const setup = useMutation({ mutationFn: () => api<Setup>(EP.auth.mfaSetup, { method: 'POST', body: {} }) });
  const enable = useMutation({
    mutationFn: () => api<{ recoveryCodes: string[] } & SessionResponse>(EP.auth.mfaEnable, { method: 'POST', body: { code: code.trim() } }),
    onSuccess: (r) => (r.recoveryCodes?.length ? setResult(r) : adopt(r)),
  });

  if (result) {
    const copy = () => void navigator.clipboard?.writeText(result.recoveryCodes.join('\n')).catch(() => undefined);
    return (
      <Frame title="Save your recovery codes" sub="Each code works once if you lose your phone. They are shown only now — keep them away from your phone.">
        <ul className="code grid grid-cols-2 gap-2 rounded-tag border border-line bg-sunken p-3 text-sm">
          {result.recoveryCodes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <div className="mt-4 flex gap-2">
          <Button variant="secondary" size="lg" className="flex-1" onClick={copy}>
            Copy
          </Button>
          <Button size="lg" className="flex-1" onClick={() => adopt(result)}>
            I saved them
          </Button>
        </div>
      </Frame>
    );
  }
  if (!setup.data) {
    return (
      <Frame title="Set up two-step sign-in" sub="Your role can approve, reconcile or see every branch, so it needs an authenticator app (Google or Microsoft Authenticator). It takes a minute.">
        <ErrorNote error={setup.error} />
        <Button size="lg" block className="mt-2" loading={setup.isPending} onClick={() => setup.mutate()}>
          Start set-up
        </Button>
        <Button variant="quiet" block className="mt-2" onClick={() => void signOut()}>
          Sign out
        </Button>
      </Frame>
    );
  }
  const s = setup.data;
  return (
    <Frame title="Scan with your authenticator" sub="Scan the code, then type the 6 digits the app shows.">
      <img src={s.qrDataUrl} alt="QR code for your authenticator app" width={184} height={184} className="mx-auto rounded-tag border border-line bg-white p-2" />
      <Note tone="neutral" className="mt-3">
        Can’t scan? Enter this key in the app: <code className="code break-all text-ink">{s.secret}</code>
      </Note>
      <form onSubmit={(e) => { e.preventDefault(); enable.mutate(); }} className="mt-4 space-y-4">
        <Input label="6-digit code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        <ErrorNote error={enable.error} />
        <Button type="submit" size="lg" block loading={enable.isPending} disabled={code.length !== 6}>
          Turn on two-step sign-in
        </Button>
      </form>
    </Frame>
  );
}
